/**
 * 登入閘門：未登入時顯示共用登入頁，登入後才渲染 app 內容。
 *
 * 包在 app 最外層即可，各 app 不需要各自處理登入狀態。
 *
 * **這不是安全邊界。**靜態輸出的 JS 全都在使用者手上，繞過這個元件只是
 * 改幾行程式的事。真正的把關在 API 那側 —— 未帶有效 session 的請求，
 * auth-service 與各後端服務一律回 401。這裡擋的是**畫面**，不是資料。
 */

import type { ReactNode } from 'react';
import { useSession } from '@shared/auth';
import { LoginScreen, type LoginScreenProps } from '../login-screen';

export interface AuthGateProps
  extends Pick<LoginScreenProps, 'appName' | 'description' | 'icon'> {
  children: ReactNode;
  /** 查詢登入狀態期間顯示的內容；未提供時用內建的極簡骨架 */
  fallback?: ReactNode;
  /**
   * 與登入狀態無關、在登入頁上也要出現的浮層（目前是安裝提示）。
   *
   * 需要這個開口，是因為登入頁由本元件自己渲染，app 沒有地方把東西疊上去。
   * 而「要不要把這個 app 留在手機上」正是還沒登入的人在想的事——擋在登入之後
   * 才提，等於只對已經回訪的人講。
   *
   * 已登入時**不會**渲染它：那時 app 自己的畫面在，浮層的位置要不要讓開 FAB
   * 或操作列，只有 app 知道。
   */
  overlay?: ReactNode;
}

export function AuthGate({
  appName,
  description,
  icon,
  fallback,
  overlay,
  children,
}: AuthGateProps) {
  const { status, error } = useSession();

  if (status === 'loading') {
    return <>{fallback ?? <LoadingScreen />}</>;
  }

  if (status === 'unauthenticated') {
    return (
      <>
        <LoginScreen
          appName={appName}
          description={description}
          icon={icon}
          /* 只在查詢真的出錯時才顯示訊息；單純未登入是正常狀態，不該像是出了問題 */
          errorMessage={
            error ? '無法確認登入狀態，請檢查網路後重新登入。' : undefined
          }
        />
        {overlay}
      </>
    );
  }

  return <>{children}</>;
}

/**
 * 載入畫面刻意做得極簡且不含文字。
 *
 * 這段通常只出現數百毫秒，放「載入中…」之類的字反而會讓人注意到閃動；
 * 一個低對比的脈動色塊足以表達「還在處理」。
 */
function LoadingScreen() {
  return (
    <div
      className="flex min-h-dvh items-center justify-center"
      role="status"
      aria-label="正在確認登入狀態"
    >
      <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
    </div>
  );
}
